import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';
import { trackEvent } from '../../analytics/sml/analytics';
import { TypedMemo } from '../../common/types';
import { publicShareKindOf } from './assetShareFacts';
import { assetShareMessages } from './assetShareMessages';
import type { OpenSharedAssetState } from './useOpenSharedAsset';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** "Open in release" for a member of the workspace of the asset; absent for every other visitor. */
export interface OpenInRelease {
  onOpen: () => void;
  state: OpenSharedAssetState;
}

/**
 * "Listen to <song>" in the artist colour: the published SmartLink of the song on its public
 * domain. Absent when the song has no published SmartLink.
 */
const ListenButtonComponent: React.FC<{ share: PublicAssetShare; className?: string }> = ({ share, className }) => {
  const intl = useIntl();
  if (share.smartLinkUrl === null) {
    return null;
  }
  return (
    <a
      className={clsx(styles.listen, className)}
      href={share.smartLinkUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="asset-share-listen"
      onClick={() =>
        trackEvent('public_share_cta_tapped', { kind: publicShareKindOf(share.assetLabel), cta: 'listen' })
      }
    >
      <FontAwesomeIcon icon={faPlay} />
      {intl.formatMessage(assetShareMessages.listenTo, { song: share.song.title })}
    </a>
  );
};

export const ListenButton = TypedMemo(ListenButtonComponent);

/** "Open in release" and its error line, for a member whose workspace switch failed. */
const OpenInReleaseButtonComponent: React.FC<{ openInRelease: OpenInRelease; onMedia?: boolean }> = ({
  openInRelease,
  onMedia = false,
}) => {
  const intl = useIntl();
  return (
    <>
      <button
        type="button"
        className={clsx(styles.ghostButton, onMedia && styles.glassButton)}
        onClick={openInRelease.onOpen}
        disabled={openInRelease.state === 'opening'}
        data-testid="asset-share-open-in-release"
      >
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        {intl.formatMessage(
          openInRelease.state === 'opening' ? assetShareMessages.openingInRelease : assetShareMessages.openInRelease
        )}
      </button>
      {openInRelease.state === 'failed' && (
        <p className={styles.alert} role="alert" data-testid="asset-share-open-failed">
          {intl.formatMessage(assetShareMessages.openInReleaseFailed)}
        </p>
      )}
    </>
  );
};

export const OpenInReleaseButton = TypedMemo(OpenInReleaseButtonComponent);

/**
 * The actions of the theater page (design asset-share c1-c5). On a desktop they sit in one row at
 * the end of the info strip: Listen, Copy link. On a phone Listen fills the width, with "Choose your
 * music app" under it; Share is the icon next to the title (`AssetShareIdentity`). There is no
 * Download action (owner decision 2026-10-19).
 */
const AssetShareActionsComponent: React.FC<{
  share: PublicAssetShare;
  onCopyLink: () => void;
  openInRelease: OpenInRelease | null;
}> = ({ share, onCopyLink, openInRelease }) => {
  const intl = useIntl();
  return (
    <div className={styles.actions}>
      <ListenButton share={share} className={styles.listenTheater} />
      {share.smartLinkUrl !== null && (
        <p className={clsx(styles.caption, 'd-lg-none')}>{intl.formatMessage(assetShareMessages.chooseApp)}</p>
      )}
      <button
        type="button"
        className={clsx(styles.ghostButton, 'd-none d-lg-inline-flex')}
        onClick={onCopyLink}
        data-testid="asset-share-copy-link"
      >
        <FontAwesomeIcon icon={faLink} />
        {intl.formatMessage(assetShareMessages.copyLink)}
      </button>
      {openInRelease !== null && <OpenInReleaseButton openInRelease={openInRelease} />}
    </div>
  );
};

export const AssetShareActions = TypedMemo(AssetShareActionsComponent);
